import { ClippedText, Combobox, IconButton, Icons, MultiSelect } from '@/shared/ui';

import styles from './AccessGrantFields.module.css';
import { useCustomRoles, useTeams } from './workspace-api';
import { ASSIGNABLE_ROLES, type MemberRole } from './types';
import { roleLabelKey } from './roles';
import { useTranslation } from 'react-i18next';

/** A subject (a team id, or `role:<standard>` / `custom:<id>`) and the role it is given. */
export interface AccessGrant {
  id: string;
  role: MemberRole;
}

interface Texts {
  title: string;
  hint: string;
  add: string;
  /** "Remove {{name}}" */
  remove: (name: string) => string;
}

function GrantRows({
  grants,
  nameOf,
  texts,
  roles,
  disabled,
  onChange,
}: {
  grants: readonly AccessGrant[];
  nameOf: (id: string) => string;
  texts: Texts;
  roles: readonly MemberRole[];
  disabled: boolean;
  onChange: (next: AccessGrant[]) => void;
}) {
  const { t } = useTranslation();
  // a role the viewer may not hand out is shown (it is what the grant has) but not offered
  const offered = (current: MemberRole) =>
    (roles.includes(current) ? roles : [...roles, current]).map((role) => ({
      value: role,
      label: t(`roles.${role}`),
    }));
  return (
    <ul className={styles.grantList}>
      {grants.map((grant) => (
        <li key={grant.id} className={styles.grantRow}>
          <ClippedText className={styles.grantName}>{nameOf(grant.id)}</ClippedText>
          <Combobox
            label={t('access.roleOf', { name: nameOf(grant.id) })}
            value={grant.role}
            size="sm"
            options={offered(grant.role)}
            disabled={disabled}
            onChange={(next) => {
              onChange(
                grants.map((g) => (g.id === grant.id ? { ...g, role: next as MemberRole } : g)),
              );
            }}
          />
          <IconButton
            label={texts.remove(nameOf(grant.id))}
            icon={<Icons.close />}
            disabled={disabled}
            onClick={() => {
              onChange(grants.filter((g) => g.id !== grant.id));
            }}
          />
        </li>
      ))}
    </ul>
  );
}

/**
 * "Teams with access": the teams of a workspace and the role each one gets. ONE component for
 * every place that hands a team access when something is created (boards, databases): the same
 * rows, the same roles, the same labels of roles. The server checks every grant again.
 */
export function TeamGrantsField({
  workspaceId,
  enabled,
  value,
  onChange,
  texts,
  roles = ASSIGNABLE_ROLES,
  disabled = false,
}: {
  workspaceId: string;
  enabled: boolean;
  value: readonly AccessGrant[];
  onChange: (next: AccessGrant[]) => void;
  texts: Texts;
  /** The roles the viewer may hand out (default: all assignable ones). */
  roles?: readonly MemberRole[];
  disabled?: boolean;
}) {
  const teams = useTeams(workspaceId, enabled);
  const nameOf = (id: string) => (teams.data ?? []).find((team) => team.id === id)?.name ?? '…';
  return (
    <div className={styles.optionBlock}>
      <span className={styles.optionTitle}>{texts.title}</span>
      <p className={styles.optionHint}>{texts.hint}</p>
      <GrantRows
        grants={value}
        nameOf={nameOf}
        texts={texts}
        roles={roles}
        disabled={disabled}
        onChange={onChange}
      />
      <MultiSelect
        label={texts.add}
        placeholder={texts.add}
        options={(teams.data ?? []).map((team) => ({ value: team.id, label: team.name }))}
        value={value.map((g) => g.id)}
        disabled={disabled}
        onChange={(ids) => {
          onChange(
            ids.map(
              (id) =>
                value.find((g) => g.id === id) ?? {
                  id,
                  role: roles.includes('editor') ? 'editor' : (roles[0] ?? 'member'),
                },
            ),
          );
        }}
      />
    </div>
  );
}

/** "Workspace roles with access": a standard or custom workspace role and the role it gets. */
export function RoleGrantsField({
  workspaceId,
  enabled,
  value,
  onChange,
  texts,
  roles = ASSIGNABLE_ROLES,
  disabled = false,
}: {
  workspaceId: string;
  enabled: boolean;
  value: readonly AccessGrant[];
  onChange: (next: AccessGrant[]) => void;
  texts: Texts;
  /** The roles the viewer may hand out (default: all assignable ones). */
  roles?: readonly MemberRole[];
  disabled?: boolean;
}) {
  const { t } = useTranslation();
  const customRoles = useCustomRoles(workspaceId, enabled);
  const nameOf = (id: string) =>
    id.startsWith('custom:')
      ? ((customRoles.data ?? []).find((role) => role.id === id.slice(7))?.name ?? '…')
      : t(roleLabelKey(id.slice(5) as MemberRole));
  return (
    <div className={styles.optionBlock}>
      <span className={styles.optionTitle}>{texts.title}</span>
      <p className={styles.optionHint}>{texts.hint}</p>
      <GrantRows
        grants={value}
        nameOf={nameOf}
        texts={texts}
        roles={roles}
        disabled={disabled}
        onChange={onChange}
      />
      <MultiSelect
        label={texts.add}
        placeholder={texts.add}
        options={[
          ...ASSIGNABLE_ROLES.map((role) => ({
            value: `role:${role}`,
            label: `${t(`roles.${role}`)} · ${t('workspaceAdmin.roleStandard')}`,
          })),
          ...(customRoles.data ?? []).map((role) => ({
            value: `custom:${role.id}`,
            label: `${role.name} · ${t('workspaceAdmin.roleCustom')}`,
          })),
        ]}
        value={value.map((g) => g.id)}
        disabled={disabled}
        onChange={(ids) => {
          onChange(
            ids.map(
              (id) =>
                value.find((g) => g.id === id) ?? {
                  id,
                  role: roles.includes('editor') ? 'editor' : (roles[0] ?? 'member'),
                },
            ),
          );
        }}
      />
    </div>
  );
}
